import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../store';
import { CustomerAssignmentsActions } from '../store/entities/customerAssignments/customerAssignments.actions';
import { CONSTANTS } from '../utils/constants';
import ActiveOrderDialog from '../pages/CustomerAssignments/ActiveOrderDialog/ActiveOrderDialog';

interface IActiveOrderData {
  customerName: string;
  phoneNumber: string;
  orderId: string | number;
}

export const useActiveOrderValidation = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showActiveOrderDialog, setShowActiveOrderDialog] =
    useState<boolean>(false);
  const [activeOrderData, setActiveOrderData] =
    useState<IActiveOrderData | null>(null);

  /**
   * Check if customer has active delivery orders
   * @param phoneNumber - Customer phone number
   * @param customerName - Customer name for display
   * @param checkMailboxOrdersOnly - Only check mailbox orders (true when only cell changed)
   * @returns true if has active orders (blocked), false if can proceed
   */
  const checkActiveOrders = async (
    phoneNumber: string,
    customerName: string,
    checkMailboxOrdersOnly: boolean = false
  ): Promise<boolean> => {
    const orderIds = await dispatch(
      CustomerAssignmentsActions.getCustomerDeliveryOrderIds(
        phoneNumber,
        checkMailboxOrdersOnly
      )
    );

    if (orderIds && orderIds.length > 0) {
      // Show dialog if there are active orders
      setActiveOrderData({
        customerName: customerName || phoneNumber,
        phoneNumber,
        orderId: orderIds[0],
      });
      setShowActiveOrderDialog(true);
      return true; // Blocked
    }

    return false; // Can proceed
  };

  const handleCloseActiveOrderDialog = () => {
    setShowActiveOrderDialog(false);
    setActiveOrderData(null);
  };

  const handleNavigateToOrder = () => {
    if (activeOrderData) {
      navigate(
        `/${CONSTANTS.ROUTES.ORDERS_MANAGEMENT.BASE}/${CONSTANTS.ROUTES.ORDERS_MANAGEMENT.CHILDREN.ORDERS_LIST}?orderId=${activeOrderData.orderId}`
      );
    }
    handleCloseActiveOrderDialog();
  };

  const renderActiveOrderDialog = () => {
    if (!showActiveOrderDialog || !activeOrderData) return null;
    return (
      <ActiveOrderDialog
        customerName={activeOrderData.customerName}
        phoneNumber={activeOrderData.phoneNumber}
        activeOrderId={activeOrderData.orderId}
        onClose={handleCloseActiveOrderDialog}
        onNavigateToOrder={handleNavigateToOrder}
      />
    );
  };

  return {
    checkActiveOrders,
    renderActiveOrderDialog,
  };
};
